import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Link } from '@/kenya_friars_components/links';
import { Button } from '@/kenya_friars_components/buttons';
import './Header.css';

interface NavItem {
  label: string;
  to?: string;
  href?: string;
  openInNewTab?: boolean;
  children?: NavItem[];
}

const navigationItems: NavItem[] = [
  { label: 'Home', to: '/' },
  { label: 'About', to: '/about' },
  {
    label: 'Apostolates',
    to: '/apostolates',
    children: [
      { label: 'Parish Apostolate', to: '/apostolates/parish' },
      { label: 'Media Apostolate', to: '/apostolates/media' },
      { label: 'Retreat Centers', to: '/apostolates/retreat-centers' },
      { label: 'Education', to: '/apostolates/education' },
      { label: 'Healthcare', to: '/apostolates/healthcare' },
      { label: 'Charitable Organizations', to: '/apostolates/charitable' },
      { label: 'Shrine Apostolate', href: '/subukia', openInNewTab: true },
    ],
  },
  { label: 'Vocations', to: '/vocations' },
  { label: 'Mass Intention', to: '/mass-intention' },
  { label: 'Blog', to: '/blog' },
  { label: 'Contact', to: '/contact' },
];

export const Header: React.FC = () => {
  const { pathname } = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);

  const isApostolatesActive = pathname === '/apostolates' || pathname.startsWith('/apostolates/');

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
    setActiveDropdown(null);
  };

  const toggleDropdown = (label: string) => {
    setActiveDropdown(activeDropdown === label ? null : label);
  };

  return (
    <>
      {/* Backdrop overlay */}
      {isMobileMenuOpen && (
        <div
          className="kf-header__backdrop"
          onClick={closeMobileMenu}
          aria-hidden="true"
        />
      )}

      <header className={`kf-header ${isMobileMenuOpen ? 'kf-header--mobile-open' : ''}`}>
        <div className="container">
          <div className="kf-header__content">
            <div className="kf-header__logo">
              <Link to="/" onClick={closeMobileMenu}>
                <img
                  src="/CONVKENYA_Transparent.png"
                  alt="Kenya Friars"
                />
              </Link>
            </div>
            
            <button
              className={`kf-header__mobile-toggle ${isMobileMenuOpen ? 'kf-header__mobile-toggle--active' : ''}`}
              onClick={toggleMobileMenu}
              aria-label="Toggle menu"
              aria-expanded={isMobileMenuOpen}
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
            
            <nav className="kf-header__nav">
              <ul className={`kf-header__menu ${isMobileMenuOpen ? 'kf-header__menu--open' : ''}`}>
                {navigationItems.map((item) => (
                  <li
                    key={item.label}
                    className={`kf-header__menu-item ${
                      item.children ? 'kf-header__menu-item--has-dropdown' : ''
                    }`}
                  >
                    {item.children ? (
                      <>
                        <button
                          className={`kf-header__menu-link ${item.label === 'Apostolates' && isApostolatesActive ? 'kf-header__menu-link--active' : ''}`}
                          onClick={() => toggleDropdown(item.label)}
                        >
                          {item.label}
                          <span className="kf-header__dropdown-arrow">▼</span>
                        </button>
                        <ul
                          className={`kf-header__dropdown ${
                            activeDropdown === item.label ? 'kf-header__dropdown--open' : ''
                          }`}
                        >
                          {item.children.map((child) => (
                            <li key={child.label}>
                              {child.href != null ? (
                                <Link
                                  href={child.href}
                                  variant="nav"
                                  external={child.openInNewTab}
                                  onClick={closeMobileMenu}
                                >
                                  {child.label}
                                </Link>
                              ) : (
                                <Link to={child.to!} variant="nav" onClick={closeMobileMenu}>
                                  {child.label}
                                </Link>
                              )}
                            </li>
                          ))}
                        </ul>
                      </>
                    ) : (
                      <Link to={item.to} variant="nav" onClick={closeMobileMenu}>
                        {item.label}
                      </Link>
                    )}
                  </li>
                ))}
                <li className="kf-header__menu-item kf-header__menu-item--donate">
                  <Button variant="gold" href="/donation" size="md" onClick={closeMobileMenu}>
                    DONATE
                  </Button>
                </li>
              </ul>
            </nav>

            <div className="kf-header__actions">
              <Button variant="gold" href="/donation" size="sm">
                DONATE
              </Button>
            </div>
          </div>
        </div>
      </header>
    </>
  );
};
